if (!customElements.get('product-discounts')) {
  class ProductDiscounts extends HTMLElement {
    constructor() {
      super();
      this.cartUpdateUnsubscriber = undefined;
      this.checkInterval = null;
      this.updateAttempts = 0;
      this.maxAttempts = 20;
      this.lastValidPrice = null;
      this.stablePriceCount = 0;
      this.requiredStableChecks = 3; // Require 3 consecutive checks with same price
      this.priceUpdateTimeout = null; // Timeout for debouncing
      this.cartUpdateTimeout = null; // Timeout for cart update debouncing
      this.discountUpdateTimeout = null; // Timeout for discount update debouncing
      this.lastUpdateTime = 0; // Track last update time to prevent rapid updates
    }

    connectedCallback() {
      // Subscribe to cart updates (debounced to prevent too many updates)
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (response) => {
        clearTimeout(this.cartUpdateTimeout);
        this.cartUpdateTimeout = setTimeout(() => {
          debugLog('[ProductsDiscount] Cart Update Event');
        this.updateDiscounts();
        }, 300);
      });
      
      // Subscribe to discount updates for real-time sync (debounced)
      this.discountUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.discountUpdate, (event) => {
        clearTimeout(this.discountUpdateTimeout);
        this.discountUpdateTimeout = setTimeout(() => {
          debugLog('[ProductsDiscount] Discount Update Event:', event);
        if (event && event.cart) {
          // Force update when discount changes
          this.updateDiscounts().then(() => {
            // CRITICAL: Ensure discount box stays visible after update
            const hasDiscount = this.querySelector('.cart__discount-summary');
            if (hasDiscount) {
              this.style.display = 'block';
              this.style.visibility = 'visible';
              debugLog('[ProductsDiscount] ✅ Discount box visible after event update');
            }
          });
        }
        }, 300);
      });
      
      // Hide initially to prevent showing incorrect prices
      this.style.display = 'none';
      this.style.visibility = 'hidden';
      
      // Check immediately on page load to catch discounts applied via URL
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        debugLog('[ProductsDiscount] 🚀 Starting initialization...');
        
        // Mark as ready when initialization completes
        Promise.resolve(this.updateDiscounts()).then(() => {
          this.dataset.ready = 'true';
          debugLog('[ProductsDiscount] ✅ Initialization complete - marked as ready');
          
          // CRITICAL: Ensure discount box is visible if discount exists
          const hasDiscount = this.querySelector('.cart__discount-summary');
          if (hasDiscount) {
            this.style.display = 'block';
            this.style.visibility = 'visible';
            debugLog('[ProductsDiscount] ✅ Discount found, showing discount box');
          }
        }).catch((error) => {
          // Even if updateDiscounts fails, mark as ready so button can be enabled
          debugWarn('[ProductsDiscount] updateDiscounts had error, but marking ready anyway:', error);
          this.dataset.ready = 'true';
          debugLog('[ProductsDiscount] ✅ Marked as ready (after error)');
        });
      });
      
      // Also check when discount events fire (event-driven, not timeout-based)
      // The cartUpdate and discountUpdate events will trigger updates
      
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.discountUpdateUnsubscriber) {
        this.discountUpdateUnsubscriber();
      }
      if (this.priceObserver) {
        this.priceObserver.disconnect();
      }
      if (this.priceUpdateTimeout) {
        clearTimeout(this.priceUpdateTimeout);
      }
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
      }
    }


    // Helper function to fetch discount summary HTML from cart footer (single source of truth)
    async fetchDiscountSummaryFromCart() {
      if (!window.routes || !window.routes.cart_url) {
        return null;
      }
      
      try {
        const response = await fetch(`${window.routes.cart_url}?section_id=main-cart-footer`);
        if (response.ok) {
          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const cartFooter = html.getElementById('main-cart-footer');
          if (cartFooter) {
            const discountSummary = cartFooter.querySelector('.cart__discount-summary');
            if (discountSummary) {
              return discountSummary.outerHTML;
            }
          }
        }
      } catch (error) {
        debugLog('[ProductsDiscount] Could not fetch discount summary from cart footer:', error);
        debugLog('[ProductsDiscount] Error details:', {
          message: error.message,
          stack: error.stack,
          cartUrl: window.routes?.cart_url
        });
      }
      return null;
    }

    async updateDiscounts() {
      try {
        // Check if discount display already shows correct discount code and amount
        const existingDisplay = this.querySelector('.cart__discount-summary');
        if (existingDisplay) {
          const existingAmountSpan = existingDisplay.querySelector('span[style*="font-size: 1.2em"], [data-discount-amount]');
          const existingCodeDivs = existingDisplay.querySelectorAll('div[style*="font-size: 0.9em"], div[style*="color: #555"]');
          const activeDiscount = localStorage.getItem('active_discount') || '';
          
          // If display exists and shows a valid discount amount (not "Will apply at checkout"),
          // and discount code matches, skip update to prevent flickering
          if (existingAmountSpan && 
              existingAmountSpan.textContent.trim() !== 'Will apply at checkout' &&
              existingAmountSpan.textContent.trim() !== '' &&
              existingCodeDivs.length > 0) {
            const displayedCode = existingCodeDivs[0]?.textContent.trim() || '';
            if (displayedCode === activeDiscount || (activeDiscount === '' && displayedCode !== '')) {
              debugLog('[ProductsDiscount] Skipping updateDiscounts - discount already correctly displayed');
              return;
            }
          }
        }
        
        // First check if we're in a bundle context and calculate bundle discount
        const bundleContext = this.closest('.bundle-product-right') || this.closest('.bundle-product');
        if (bundleContext) {
          const hasDiscount = await this.updateBundleDiscounts(bundleContext);
          // CRITICAL: Ensure discount box is visible if discount exists, even if updateBundleDiscounts returns false
          if (hasDiscount || this.querySelector('.cart__discount-summary')) {
            this.style.display = 'block';
            this.style.visibility = 'visible';
            debugLog('[ProductsDiscount] ✅ Showing discount box after updateBundleDiscounts');
          }
          if (hasDiscount) {
            return;
          }
          // If in bundle context but no discount found, check if we have discount code anyway
          const activeDiscount = localStorage.getItem('active_discount') || '';
          const url = new URL(window.location.href);
          const pathMatch = url.pathname.match(/^\/discount\/([^\/]+)/i);
          const pathDiscount = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
          const queryDiscount = new URLSearchParams(window.location.search).get('discount');
          if (activeDiscount || pathDiscount || queryDiscount) {
            // We have a discount code, show it even if updateBundleDiscounts didn't show it
            debugLog('[ProductsDiscount] Found discount code but updateBundleDiscounts returned false, showing anyway');
            return; // Will be handled by updateBundleDiscounts fallback logic
          }
          return;
        }

        // Otherwise, fetch from cart footer (only if not in bundle context)
        if (!window.routes || !window.routes.cart_url) {
          return;
        }
        
        try {
          const response = await fetch(`${window.routes.cart_url}?section_id=main-cart-footer`);
          if (!response.ok) {
            return;
          }
          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const cartFooter = html.getElementById('main-cart-footer');
          
          // Always check cart.js first to get discount codes (even if cart is empty)
          let discountCodeHtml = '';
          let discountAmount = '';
          let hasDiscount = false;
          
          try {
            const cartResponse = await fetch('/cart.js');
            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              
              debugLog('[ProductsDiscount] Cart Check');
              debugLog('[ProductsDiscount] Cart discount_codes:', cart.discount_codes);
              debugLog('[ProductsDiscount] Cart items:', cart.items?.length || 0);
              
              // Check if cart has discount codes (even if empty or applicable: false)
              // Show discount codes even if they're not applicable yet (they'll apply when items are added)
              if (cart.discount_codes && cart.discount_codes.length > 0) {
                hasDiscount = true;
                const discountCode = typeof cart.discount_codes[0] === 'string' 
                  ? cart.discount_codes[0] 
                  : cart.discount_codes[0].code;
                const applicable = typeof cart.discount_codes[0] === 'object' 
                  ? cart.discount_codes[0].applicable 
                  : true;
                
                debugLog('[ProductsDiscount] ✅ Found discount code in cart:', discountCode, 'Applicable:', applicable);
                
                // Show ALL discount codes, even if applicable: false (they'll apply when items are added)
                discountCodeHtml = cart.discount_codes.map(code => {
                  const codeValue = typeof code === 'string' ? code : code.code;
                  const codeApplicable = typeof code === 'object' ? code.applicable : true;
                  return `<div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
                    <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                    </svg>
                    ${codeValue}${codeApplicable === false ? ' (will apply at checkout)' : ''}
                  </div>`;
                }).join('');
                
                // Calculate discount amount if cart has items
                if (cart.items && cart.items.length > 0) {
                  let cartOriginalTotal = 0;
                  cart.items.forEach(item => {
                    // original_line_price is in cents, use it if available, otherwise use line_price
                    cartOriginalTotal += (item.original_line_price || item.line_price || 0);
                  });
                  const cartTotalPrice = cart.total_price || 0;
                  const actualDiscount = cartOriginalTotal - cartTotalPrice;
                  
                  debugLog('[ProductsDiscount] Discount calculation (non-bundle):', {
                    cartOriginalTotal,
                    cartTotalPrice,
                    actualDiscount,
                    items: cart.items.length
                  });
                  
                  if (actualDiscount > 0) {
                    const formatted = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2
                    }).format(actualDiscount / 100);
                    discountAmount = `-${formatted} USD`;
                    debugLog('[ProductsDiscount] ✅ Calculated discount amount:', discountAmount);
                  } else {
                    debugLog('[ProductsDiscount] ⚠️ Discount calculation resulted in 0 or negative value');
                    discountAmount = 'Will apply at checkout';
                  }
                } else {
                  // Cart is empty but has discount code - show message that discount will apply
                  discountAmount = 'Will apply at checkout';
                }
              } else if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
                hasDiscount = true;
                debugLog('[ProductsDiscount] ✅ Found cart level discount:', cart.cart_level_discount_applications[0].title);
                
                discountCodeHtml = cart.cart_level_discount_applications.map(discount => 
                  `<div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
                    <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                    </svg>
                    ${discount.title}
                  </div>`
                ).join('');
                
                // Calculate discount amount if cart has items
                if (cart.items && cart.items.length > 0) {
                  let cartOriginalTotal = 0;
                  cart.items.forEach(item => {
                    // original_line_price is in cents, use it if available, otherwise use line_price
                    cartOriginalTotal += (item.original_line_price || item.line_price || 0);
                  });
                  const cartTotalPrice = cart.total_price || 0;
                  const actualDiscount = cartOriginalTotal - cartTotalPrice;
                  
                  debugLog('[ProductsDiscount] Discount calculation (non-bundle cart_level):', {
                    cartOriginalTotal,
                    cartTotalPrice,
                    actualDiscount,
                    items: cart.items.length
                  });
                  
                  if (actualDiscount > 0) {
                    const formatted = new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2
                    }).format(actualDiscount / 100);
                    discountAmount = `-${formatted} USD`;
                    debugLog('[ProductsDiscount] ✅ Calculated discount amount:', discountAmount);
                  } else {
                    debugLog('[ProductsDiscount] ⚠️ Discount calculation resulted in 0 or negative value');
                    discountAmount = 'Will apply at checkout';
                  }
                } else {
                  discountAmount = 'Will apply at checkout';
                }
              } else {
                // No discount in cart - check URL pathname, query parameters, and localStorage
                const url = new URL(window.location.href);
                const pathMatch = url.pathname.match(/^\/discount\/([^\/]+)/i);
                const pathDiscount = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
                const urlParams = new URLSearchParams(window.location.search);
                const queryDiscount = urlParams.get('discount');
                const localStorageDiscount = localStorage.getItem('active_discount');
                const activeDiscount = pathDiscount || queryDiscount || localStorageDiscount;
                
                if (activeDiscount) {
                  debugLog('No discount in cart, but found in URL/localStorage:', activeDiscount);
                  debugLog('Path discount:', pathDiscount);
                  debugLog('Query discount:', queryDiscount);
                  debugLog('localStorage discount:', localStorageDiscount);
                  debugLog('Showing discount from URL/localStorage (will apply when items added)');
                  
                  // Store in localStorage if from URL (path or query)
                  const urlDiscount = pathDiscount || queryDiscount;
                  if (urlDiscount && urlDiscount !== localStorageDiscount) {
                    localStorage.setItem('active_discount', urlDiscount);
                  }
                  
                  // Show the discount even if not in cart yet (it will apply when items are added)
                  hasDiscount = true;
                  discountCodeHtml = `<div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
                    <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                    </svg>
                    ${activeDiscount}
                  </div>`;
                  discountAmount = 'Will apply at checkout';
                  
                  // Also try to apply it to cart
                  debugLog('Attempting to apply discount from localStorage to cart:', activeDiscount);
                  
                  // First check if there are inapplicable discount codes and remove them
                  fetch('/cart.js')
                    .then(response => response.json())
                    .then(cart => {
                      // Remove any existing discount codes that are not applicable
                      const removePromises = [];
                      if (cart.discount_codes && cart.discount_codes.length > 0) {
                        cart.discount_codes.forEach(discountCode => {
                          const code = typeof discountCode === 'string' ? discountCode : discountCode.code;
                          const applicable = typeof discountCode === 'object' ? discountCode.applicable : true;
                          // Remove if not applicable or if it's different from the one we want to apply
                          if (!applicable || code !== activeDiscount) {
                            debugLog('Removing inapplicable or conflicting discount code:', code);
                            removePromises.push(
                              fetch('/cart/update.js', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  discount: code,
                                  updates: {}
                                })
                              })
                            );
                          }
                        });
                      }
                      
                      // Wait for removals, then apply new discount
                      return Promise.all(removePromises).then(() => {
                        return fetch('/cart/update.js', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            discount: activeDiscount
                          })
                        });
                      });
                    })
                    .then(response => response.json())
                    .then(updatedCart => {
                      debugLog('Discount applied, updated cart:', updatedCart);
                      debugLog('Discount codes after apply:', updatedCart.discount_codes);
                      if (updatedCart.discount_codes && updatedCart.discount_codes.length > 0) {
                        // Retry updateDiscounts after applying
                        this.updateDiscounts();
                      }
                    })
                    .catch(error => {
                      debugError('Error applying discount from localStorage:', error);
                    });
                }
              }
            }
          } catch (error) {
            debugLog('Error fetching cart for discount:', error);
          }
          
          if (cartFooter) {
            const discountSummary = cartFooter.querySelector('.cart__discount-summary');
            
            if (discountSummary) {
              // Get discount amount from the summary if we don't have it yet
              if (!discountAmount) {
                const discountAmountElement = discountSummary.querySelector('span[style*="font-size: 1.2em"]');
                discountAmount = discountAmountElement ? discountAmountElement.textContent.trim() : '';
              }
              
              // If we don't have discount code HTML yet, try to extract from summary
              if (!discountCodeHtml) {
                try {
                  const existingCodeElements = discountSummary.querySelectorAll('div[style*="font-size: 0.9em"], div[style*="color: #555"]');
                  if (existingCodeElements.length > 0) {
                    discountCodeHtml = Array.from(existingCodeElements).map(el => {
                      const text = el.textContent.trim();
                      if (el.querySelector('.icon-discount')) {
                        return el.outerHTML;
                      }
                      return `<div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
                        <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                        </svg>
                        ${text}
                      </div>`;
                    }).join('');
                  }
                } catch (error) {
                  // Silently fail
                }
              }
              
              // Show discount if we have either amount or code
              if (discountAmount || discountCodeHtml) {
                hasDiscount = true;
              }
            }
          }
          
          // Show discount if we found one (even if cart is empty)
          if (hasDiscount && (discountAmount || discountCodeHtml)) {
            // ALWAYS fetch from cart footer to use the same component (single source of truth)
            const discountSummaryHtml = await this.fetchDiscountSummaryFromCart();
            if (discountSummaryHtml) {
              // Parse the HTML and ensure discount amount is correct
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = discountSummaryHtml;
              const discountSummary = tempDiv.querySelector('.cart__discount-summary');
              
              if (discountSummary) {
                // Check if discount display already exists - if so, update incrementally instead of replacing
                const existingDisplay = this.querySelector('.cart__discount-summary');
                if (existingDisplay) {
                  // Update only text content to prevent flickering
                  const existingAmountSpan = existingDisplay.querySelector('span[style*="font-size: 1.2em"], [data-discount-amount]');
                  if (existingAmountSpan && discountAmount && discountAmount !== 'Will apply at checkout') {
                    existingAmountSpan.textContent = discountAmount;
                  }
                  
                  // Update discount code name if needed
                  const discountCodeDivs = discountSummary.querySelectorAll('div[style*="font-size: 0.9em"], div[style*="color: #555"]');
                  const existingCodeDivs = existingDisplay.querySelectorAll('div[style*="font-size: 0.9em"], div[style*="color: #555"]');
                  if (discountCodeDivs.length > 0 && existingCodeDivs.length > 0) {
                    discountCodeDivs.forEach((newDiv, index) => {
                      if (existingCodeDivs[index]) {
                        existingCodeDivs[index].textContent = newDiv.textContent;
                      }
                    });
                  }
                  
              // Ensure display is visible
              this.style.display = 'block';
              this.style.visibility = 'visible';
              debugLog('[ProductsDiscount] ✅ Updated existing discount display incrementally - showing box');
              return;
                }
                
                // If display doesn't exist, create it
                // Find the discount amount span and update it if we have a calculated value
                const discountAmountSpan = discountSummary.querySelector('span[style*="font-size: 1.2em"]');
                if (discountAmountSpan && discountAmount && discountAmount !== 'Will apply at checkout') {
                  // Use our calculated discount amount if it's available and valid
                  discountAmountSpan.textContent = discountAmount;
                } else if (discountAmountSpan && discountAmountSpan.textContent.trim() === 'Will apply at checkout' && discountAmount && discountAmount !== 'Will apply at checkout') {
                  // Replace "Will apply at checkout" with actual discount amount if we have it
                  discountAmountSpan.textContent = discountAmount;
                }
                
                this.innerHTML = discountSummary.outerHTML;
              this.style.display = 'block';
              this.style.visibility = 'visible';
              return;
            }
            }
            
            // If fetch fails but we have discount from URL/localStorage, show it anyway
            if (hasDiscount && (discountAmount || discountCodeHtml)) {
              debugLog('Cart footer fetch failed, but showing discount from URL/localStorage');
              this.innerHTML = `
                <div class="cart__discount-summary" style="margin-bottom: 1rem; padding: 1rem; background: #f0f8f0; border-radius: 4px; text-align: left;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                    <span style="font-weight: 600; color: #2d5016;">Discount Applied:</span>
                    <span style="font-weight: 700; color: #2d5016; font-size: 1.2em;">${discountAmount || 'Will apply at checkout'}</span>
                  </div>
                  ${discountCodeHtml}
                </div>
              `;
              this.style.display = 'block';
              this.style.visibility = 'visible';
              return;
            }
            
            // Only if fetch fails and no discount found, don't show anything (let cart footer handle it)
            // This ensures we never duplicate the discount display
            debugLog('Could not fetch discount summary from cart footer, hiding product discount display');
            this.style.display = 'none';
            this.style.visibility = 'hidden';
          } else {
            // Fallback to simple discount list if summary doesn't exist
            if (cartFooter) {
              const discountsContainer = cartFooter.querySelector('.discounts');
              if (discountsContainer && discountsContainer.children.length > 0) {
                const discountsHTML = discountsContainer.outerHTML;
                this.innerHTML = `<div class="product__discounts">${discountsHTML}</div>`;
                this.style.display = 'block';
                this.style.visibility = 'visible';
              } else {
                // No discounts, clear the display
                this.innerHTML = '';
                this.style.display = 'none';
                this.style.visibility = 'hidden';
              }
            }
          }
        } catch (fetchError) {
          // Silently fail - don't log fetch errors as they're expected in some contexts
          return;
        }
      } catch (error) {
        // Only log unexpected errors, not fetch failures
        if (error.name !== 'TypeError' || !error.message.includes('Load failed')) {
          debugError('Error updating product discounts:', error);
        }
      }
    }

    async updateBundleDiscounts(bundleContext) {
      // ALWAYS check cart first for discount codes, even if bundle prices aren't ready
      // This ensures discount shows immediately when discount URL is visited
      let discountFormatted = '';
      let discountCodeHtml = '';
      let hasCartDiscount = false;
      
      // FIRST: Check URL pathname (/discount/CODE), query parameters (?discount=CODE), and localStorage
      const url = new URL(window.location.href);
      const pathMatch = url.pathname.match(/^\/discount\/([^\/]+)/i);
      const pathDiscount = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
      const urlParams = new URLSearchParams(window.location.search);
      const queryDiscount = urlParams.get('discount');
      const localStorageDiscount = localStorage.getItem('active_discount');
      const activeDiscountCode = pathDiscount || queryDiscount || localStorageDiscount;
      
        if (activeDiscountCode) {
          debugLog('[ProductsDiscount] Found discount in URL/localStorage');
          debugLog('[ProductsDiscount] Path discount:', pathDiscount);
          debugLog('[ProductsDiscount] Query discount:', queryDiscount);
          debugLog('[ProductsDiscount] localStorage discount:', localStorageDiscount);
          debugLog('[ProductsDiscount] Active discount code:', activeDiscountCode);
          
          // Store in localStorage if from URL (path or query)
          const urlDiscount = pathDiscount || queryDiscount;
          if (urlDiscount && urlDiscount !== localStorageDiscount) {
            localStorage.setItem('active_discount', urlDiscount);
          }
        
        hasCartDiscount = true;
        discountCodeHtml = `<div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
          <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
          </svg>
          ${activeDiscountCode}
        </div>`;
        
        // PRODUCT PAGE: Calculate discount based on THIS product's bundle price only
        // This ensures we show the discount for the product being viewed (e.g., -$2,350.00)
        // NOT the total discount for all items in cart (e.g., -$7,050.00)
        // The cart page has its own component that shows total cart discount.
        
        // Try to calculate discount from bundle price if available
        // The bundle price might not be ready yet, so we'll try multiple times
        let bundlePriceResult = this.calculateDiscountFromBundlePrice(bundleContext, activeDiscountCode);
        
        // Function to update discount display
        const updateDiscountDisplay = (result) => {
          // Throttle updates to prevent too many calls
          const now = Date.now();
          if (now - this.lastUpdateTime < 500) {
            debugLog('[ProductsDiscount] Throttling updateDiscountDisplay');
            return;
          }
          this.lastUpdateTime = now;
          
          debugLog('[ProductsDiscount] updateDiscountDisplay called with:', result);
          if (result && result.discountAmount > 0) {
            discountFormatted = result.discountFormatted;
          } else {
            debugLog('[ProductsDiscount] updateDiscountDisplay: Invalid result, skipping', result);
          }
        };
        
        // If we got a result, use it immediately
        if (bundlePriceResult && bundlePriceResult.discountAmount > 0) {
          updateDiscountDisplay(bundlePriceResult);
        } else {
          discountFormatted = 'Will apply at checkout';
        }
        
        // If price not found, wait for breakdown display via event-driven approach
        // No setTimeout retries - breakdown display will trigger discount update when ready
        if (!bundlePriceResult && bundleContext) {
          // Subscribe to variant change events to recalculate when price becomes available
          if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            const variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
              const updatedResult = this.calculateDiscountFromBundlePrice(bundleContext, activeDiscountCode);
              if (updatedResult && updatedResult.discountAmount > 0) {
                updateDiscountDisplay(updatedResult);
                const amountSpan = this.querySelector('span[style*="font-size: 1.2em"], [data-discount-amount]');
                if (amountSpan && amountSpan.textContent === 'Will apply at checkout') {
                  amountSpan.textContent = updatedResult.discountFormatted;
                }
                variantChangeUnsubscriber(); // Unsubscribe after first successful update
              }
            });
          }
        }
      }
      
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          
          debugLog('[ProductsDiscount] Cart Check');
          debugLog('[ProductsDiscount] Cart discount_codes:', cart.discount_codes);
          debugLog('[ProductsDiscount] Cart items:', cart.items?.length || 0);
          
          // CRITICAL: If we already have discountFormatted from activeDiscountCode (localStorage/URL),
          // and cart has a different code, skip cart update to prevent override
          // This ensures VIP55 from localStorage is not overridden by Subscriber from cart
          if (discountFormatted && discountFormatted !== 'Will apply at checkout' && activeDiscountCode) {
            const cartDiscountCode = cart.discount_codes && cart.discount_codes.length > 0
              ? (typeof cart.discount_codes[0] === 'string' ? cart.discount_codes[0] : cart.discount_codes[0].code)
              : (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0
                  ? cart.cart_level_discount_applications[0]?.title : null);
            
            if (cartDiscountCode && cartDiscountCode.toLowerCase() !== activeDiscountCode.toLowerCase()) {
              debugLog('[ProductsDiscount] Skipping cart update - activeDiscountCode takes priority:', {
                activeCode: activeDiscountCode,
                cartCode: cartDiscountCode,
                currentDiscount: discountFormatted
              });
              // Skip cart update - use the discount we already calculated from activeDiscountCode
            } else {
              // Cart code matches or no cart code, proceed with cart update
              hasCartDiscount = true;
            }
          } else {
            hasCartDiscount = true;
          }
          
          // Check if cart has discount codes (only if we're proceeding with cart update)
          if (hasCartDiscount && cart.discount_codes && cart.discount_codes.length > 0) {
            const cartDiscountCode = typeof cart.discount_codes[0] === 'string' 
              ? cart.discount_codes[0] 
              : cart.discount_codes[0].code;
            
            debugLog('[ProductsDiscount] ✅ Found discount code in cart:', cartDiscountCode);
            
            // CRITICAL: Prioritize activeDiscountCode from localStorage/URL over cart discount code
            // This prevents cart discount from overriding the correct discount that user wants
            // (e.g., cart has "Subscriber" but localStorage has "VIP55" - use VIP55)
            const discountCodeToUse = activeDiscountCode || cartDiscountCode;
            const isUsingActiveCode = activeDiscountCode && activeDiscountCode !== cartDiscountCode;
            
            if (isUsingActiveCode) {
              debugLog('[ProductsDiscount] Using activeDiscountCode from localStorage/URL instead of cart:', {
                activeCode: activeDiscountCode,
                cartCode: cartDiscountCode
              });
            }
            
            // Build discount code HTML with icon - use the code we're actually using
            discountCodeHtml = `<div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
              <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
              </svg>
              ${discountCodeToUse}
            </div>`;
            
            // PRODUCT PAGE: Always calculate discount based on THIS product's bundle price,
            // NOT the cart total. This ensures we show the discount for THIS product only
            // (e.g., -$2,350.00 for one bike), not the total discount for all items in cart
            // (e.g., -$7,050.00 for 3 bikes). The cart page has its own component.
            
            // Calculate discount from bundle price (product page price) using the prioritized code
            let bundlePriceResult = this.calculateDiscountFromBundlePrice(bundleContext, discountCodeToUse);
            
            if (bundlePriceResult && bundlePriceResult.discountAmount > 0) {
              discountFormatted = bundlePriceResult.discountFormatted;
              debugLog('[ProductsDiscount] ✅ Calculated product page discount (from bundle price):', discountFormatted, 'using code:', discountCodeToUse);
            } else {
              // Bundle price not ready yet, will be calculated later
              discountFormatted = 'Will apply at checkout';
              debugLog('[ProductsDiscount] Bundle price not ready, will calculate discount when price loads');
            }
          } else if (hasCartDiscount && cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
            
            // Get discount code name from cart_level_discount_applications
            const cartDiscountTitle = cart.cart_level_discount_applications[0]?.title || '';
            
            // CRITICAL: Prioritize activeDiscountCode from localStorage/URL over cart discount
            // This prevents cart discount from overriding the correct discount that user wants
            const discountCodeToUse = activeDiscountCode || cartDiscountTitle;
            const isUsingActiveCode = activeDiscountCode && activeDiscountCode !== cartDiscountTitle;
            
            if (isUsingActiveCode) {
              debugLog('[ProductsDiscount] Using activeDiscountCode from localStorage/URL instead of cart:', {
                activeCode: activeDiscountCode,
                cartCode: cartDiscountTitle
              });
            }
            
            // Build discount code HTML with icon - use the code we're actually using
            discountCodeHtml = `<div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
              <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
              </svg>
              ${discountCodeToUse}
            </div>`;
            
            // PRODUCT PAGE: Always calculate discount based on THIS product's bundle price,
            // NOT the cart total. This ensures we show the discount for THIS product only
            // (e.g., -$2,350.00 for one bike), not the total discount for all items in cart
            // (e.g., -$7,050.00 for 3 bikes). The cart page has its own component.
            
            // Calculate discount from bundle price (product page price) using the prioritized code
            let bundlePriceResult = this.calculateDiscountFromBundlePrice(bundleContext, discountCodeToUse);
            
            if (bundlePriceResult && bundlePriceResult.discountAmount > 0) {
              discountFormatted = bundlePriceResult.discountFormatted;
              debugLog('[ProductsDiscount] ✅ Calculated product page discount (from bundle price):', discountFormatted, 'using code:', discountCodeToUse);
            } else {
              // Bundle price not ready yet, will be calculated later
              discountFormatted = 'Will apply at checkout';
              debugLog('[ProductsDiscount] Bundle price not ready, will calculate discount when price loads');
            }
          }
        }
      } catch (error) {
        debugLog('[ProductsDiscount] Error fetching cart in updateBundleDiscounts:', error);
      }
      
      // If we found discount (from cart, URL, or localStorage), show it
      // Show discount even if cart is empty (discount code is applied and will apply when items are added)
      if (hasCartDiscount && discountCodeHtml) {
        // ALWAYS fetch from cart footer (single source of truth)
        const discountSummaryHtml = await this.fetchDiscountSummaryFromCart();
        if (discountSummaryHtml) {
          // Parse the HTML and ensure discount amount is correct
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = discountSummaryHtml;
          const discountSummary = tempDiv.querySelector('.cart__discount-summary');
          
          if (discountSummary) {
            // Find the discount amount span and update it if we have a calculated value
            const discountAmountSpan = discountSummary.querySelector('span[style*="font-size: 1.2em"]');
            if (discountAmountSpan && discountFormatted && discountFormatted !== 'Will apply at checkout') {
              // Use our calculated discount amount if it's available and valid
              discountAmountSpan.textContent = discountFormatted;
            } else if (discountAmountSpan && discountAmountSpan.textContent.trim() === 'Will apply at checkout' && discountFormatted && discountFormatted !== 'Will apply at checkout') {
              // Replace "Will apply at checkout" with actual discount amount if we have it
              discountAmountSpan.textContent = discountFormatted;
            }
            
            // Ensure icon is preserved - check if icon exists, if not add it
            // Check if discount display already exists - if so, update incrementally instead of replacing
            const existingDisplay = this.querySelector('.cart__discount-summary');
            if (existingDisplay) {
              // Update only text content to prevent flickering
              const existingAmountSpan = existingDisplay.querySelector('span[style*="font-size: 1.2em"], [data-discount-amount]');
              if (existingAmountSpan && discountFormatted && discountFormatted !== 'Will apply at checkout') {
                existingAmountSpan.textContent = discountFormatted;
              }
              
              // Update discount code name if needed
              const discountCodeDivs = discountSummary.querySelectorAll('div[style*="font-size: 0.9em"], div[style*="color: #555"]');
              const existingCodeDivs = existingDisplay.querySelectorAll('div[style*="font-size: 0.9em"], div[style*="color: #555"]');
              if (discountCodeDivs.length > 0 && existingCodeDivs.length > 0) {
                discountCodeDivs.forEach((newDiv, index) => {
                  if (existingCodeDivs[index]) {
                    existingCodeDivs[index].textContent = newDiv.textContent;
                  }
                });
              }
              
              // Ensure display is visible
              this.style.display = 'block';
              this.style.visibility = 'visible';
              debugLog('[ProductsDiscount] ✅ Updated existing discount display incrementally (updateBundleDiscounts) - showing box');
              return true;
            }
            
            // If display doesn't exist, create it
            // Ensure icon is preserved - check if icon exists, if not add it
            const discountCodeDivs = discountSummary.querySelectorAll('div[style*="font-size: 0.9em"]');
            discountCodeDivs.forEach(div => {
              const hasIcon = div.querySelector('svg.icon-discount') || div.querySelector('.icon-discount');
              if (!hasIcon) {
                // Icon missing, add it
                const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                iconSvg.setAttribute('aria-hidden', 'true');
                iconSvg.setAttribute('focusable', 'false');
                iconSvg.setAttribute('class', 'icon icon-discount');
                iconSvg.setAttribute('viewBox', '0 0 12 12');
                iconSvg.setAttribute('style', 'width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));');
                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path.setAttribute('fill-rule', 'evenodd');
                path.setAttribute('clip-rule', 'evenodd');
                path.setAttribute('d', 'M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z');
                path.setAttribute('fill', 'currentColor');
                iconSvg.appendChild(path);
                
                // Insert icon at the beginning of the div
                const firstChild = div.firstChild;
                if (firstChild) {
                  div.insertBefore(iconSvg, firstChild);
                } else {
                  div.appendChild(iconSvg);
                }
              }
            });
            
            this.innerHTML = discountSummary.outerHTML;
          this.style.display = 'block';
          this.style.visibility = 'visible';
          debugLog('[ProductsDiscount] ✅ Showing discount box with cart footer HTML');
          return true;
        }
        }
        
        // If fetch fails or doesn't have discount amount, generate markup matching the snippet
        // CRITICAL: Always show the discount code even if cart is empty (from URL/localStorage)
        debugLog('Generating discount summary markup with discount code');
        
        // Try one more time to get discount amount before displaying
        if (!discountFormatted || discountFormatted === 'Will apply at checkout') {
          const finalBundlePriceResult = this.calculateDiscountFromBundlePrice(bundleContext, activeDiscountCode);
          if (finalBundlePriceResult && finalBundlePriceResult.discountAmount > 0) {
            discountFormatted = finalBundlePriceResult.discountFormatted;
            debugLog('✅ Got discount amount before display:', discountFormatted);
          }
        }
        
        this.innerHTML = `
          <div class="cart__discount-summary" style="margin-bottom: 1rem; padding: 1rem; background: #f0f8f0; border-radius: 4px; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
              <span style="font-weight: 600; color: #2d5016;">Discount Applied:</span>
              <span style="font-weight: 700; color: #2d5016; font-size: 1.2em;" data-discount-amount>${discountFormatted || 'Will apply at checkout'}</span>
            </div>
            ${discountCodeHtml}
          </div>
        `;
        this.style.display = 'block';
        this.style.visibility = 'visible';
        debugLog('[ProductsDiscount] ✅ Showing discount box with generated HTML');
        
        // Continue watching for breakdown display updates via event-driven approach
        if (discountFormatted === 'Will apply at checkout' && bundleContext) {
          // Subscribe to variant change events to update when breakdown becomes ready
          if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            const variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
              const updatedResult = this.calculateDiscountFromBundlePrice(bundleContext, activeDiscountCode);
              if (updatedResult && updatedResult.discountAmount > 0) {
                const amountSpan = this.querySelector('[data-discount-amount]');
                if (amountSpan && amountSpan.textContent === 'Will apply at checkout') {
                  amountSpan.textContent = updatedResult.discountFormatted;
                  debugLog('✅ Updated discount amount after breakdown loaded:', updatedResult.discountFormatted);
                  variantChangeUnsubscriber(); // Unsubscribe after first successful update
                }
              }
            });
          }
        }
        
        return true;
      }
      
      // Also check URL pathname, query params, and localStorage if we didn't find discount in cart
      if (!hasCartDiscount) {
        const url = new URL(window.location.href);
        const pathMatch = url.pathname.match(/^\/discount\/([^\/]+)/i);
        const pathDiscount = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
        const urlParams = new URLSearchParams(window.location.search);
        const queryDiscount = urlParams.get('discount');
        const localStorageDiscount = localStorage.getItem('active_discount');
        const activeDiscountCode = pathDiscount || queryDiscount || localStorageDiscount;
        
        if (activeDiscountCode) {
          debugLog('=== UPDATE BUNDLE DISCOUNTS: Found discount in URL/localStorage (cart empty) ===');
          debugLog('Path discount:', pathDiscount);
          debugLog('Query discount:', queryDiscount);
          debugLog('Active discount code:', activeDiscountCode);
          
          // Store in localStorage if from URL (path or query)
          const urlDiscount = pathDiscount || queryDiscount;
          if (urlDiscount && urlDiscount !== localStorageDiscount) {
            localStorage.setItem('active_discount', urlDiscount);
          }
          
          // Try to calculate discount from bundle price if available
          // The bundle price might not be ready yet, so we'll try multiple times
          let discountFormatted = 'Will apply at checkout';
          let bundlePriceResult = this.calculateDiscountFromBundlePrice(bundleContext, activeDiscountCode);
          
          // Function to update discount display
          const updateDiscountDisplay = (result) => {
            if (result && result.discountAmount > 0) {
              discountFormatted = result.discountFormatted;
            }
          };
          
          // If we got a result, use it immediately
          if (bundlePriceResult && bundlePriceResult.discountAmount > 0) {
            updateDiscountDisplay(bundlePriceResult);
          }
          
          // If price not found, wait for breakdown display via event-driven approach
          if (!bundlePriceResult && bundleContext) {
            // Subscribe to variant change events to recalculate when price becomes available
            if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              const variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
                const updatedResult = this.calculateDiscountFromBundlePrice(bundleContext, activeDiscountCode);
                if (updatedResult && updatedResult.discountAmount > 0) {
                  updateDiscountDisplay(updatedResult);
                  const amountSpan = this.querySelector('span[style*="font-size: 1.2em"], [data-discount-amount]');
                  if (amountSpan && amountSpan.textContent === 'Will apply at checkout') {
                    amountSpan.textContent = updatedResult.discountFormatted;
                  }
                  variantChangeUnsubscriber(); // Unsubscribe after first successful update
                }
              });
            }
          }
          
          // Show discount even though cart is empty
          this.innerHTML = `
            <div class="cart__discount-summary" style="margin-bottom: 1rem; padding: 1rem; background: #f0f8f0; border-radius: 4px; text-align: left;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                <span style="font-weight: 600; color: #2d5016;">Discount Applied:</span>
                <span style="font-weight: 700; color: #2d5016; font-size: 1.2em;">${discountFormatted}</span>
              </div>
              <div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
                <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                </svg>
                ${activeDiscountCode}
              </div>
            </div>
          `;
          this.style.display = 'block';
          this.style.visibility = 'visible';
          debugLog('[ProductsDiscount] ✅ Showing discount box (cart empty, URL/localStorage discount)');
          return true;
        }
      }
      
      // Legacy price element queries removed - we now use breakdown display exclusively
      // Discount information comes from cart or product-discounts element display
      const breakdownContainer = bundleContext.querySelector('.bundle-price-breakdown');
      const isBreakdownShowing = breakdownContainer && breakdownContainer.style.display !== 'none';
      
      // If breakdown is showing, try to calculate discount from it
      if (isBreakdownShowing) {
        const breakdownActiveDiscountCode = localStorage.getItem('active_discount') || '';
        if (breakdownActiveDiscountCode) {
          // Try to calculate discount from breakdown
          const bundlePriceResult = this.calculateDiscountFromBundlePrice(bundleContext, breakdownActiveDiscountCode);
          if (bundlePriceResult && bundlePriceResult.discountAmount > 0) {
            // Show discount with calculated amount
            this.innerHTML = `
              <div class="cart__discount-summary" style="margin-bottom: 1rem; padding: 1rem; background: #f0f8f0; border-radius: 4px; text-align: left;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                  <span style="font-weight: 600; color: #2d5016;">Discount Applied:</span>
                  <span style="font-weight: 700; color: #2d5016; font-size: 1.2em;" data-discount-amount>${bundlePriceResult.discountFormatted}</span>
                </div>
                <div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
                  <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                  </svg>
                  ${breakdownActiveDiscountCode}
                </div>
              </div>
            `;
            this.style.display = 'block';
            this.style.visibility = 'visible';
            return true;
          } else {
            // Breakdown showing but discount not calculated yet, show "Will apply at checkout"
            this.innerHTML = `
              <div class="cart__discount-summary" style="margin-bottom: 1rem; padding: 1rem; background: #f0f8f0; border-radius: 4px; text-align: left;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                  <span style="font-weight: 600; color: #2d5016;">Discount Applied:</span>
                  <span style="font-weight: 700; color: #2d5016; font-size: 1.2em;" data-discount-amount>Will apply at checkout</span>
                </div>
                <div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
                  <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                  </svg>
                  ${breakdownActiveDiscountCode}
                </div>
              </div>
            `;
            this.style.display = 'block';
            this.style.visibility = 'visible';
            return true;
          }
        }
        // No active discount code, hide discount box
        this.innerHTML = '';
        this.style.display = 'none';
        this.style.visibility = 'hidden';
        return false;
      }
      
      // Breakdown not showing - check if we have an active discount code to preserve display
      const preservedActiveDiscountCode = localStorage.getItem('active_discount') || '';
      if (preservedActiveDiscountCode) {
        // Preserve discount display even if breakdown is temporarily hidden
        // Try to get last known discount amount from existing display
        const existingDisplay = this.querySelector('.cart__discount-summary');
        let discountFormatted = 'Will apply at checkout';
        
        if (existingDisplay) {
          const existingAmountSpan = existingDisplay.querySelector('[data-discount-amount]');
          if (existingAmountSpan && existingAmountSpan.textContent.trim() !== 'Will apply at checkout' && existingAmountSpan.textContent.trim() !== '') {
            discountFormatted = existingAmountSpan.textContent.trim();
          }
        }
        
        // Show discount with preserved or default message
        this.innerHTML = `
          <div class="cart__discount-summary" style="margin-bottom: 1rem; padding: 1rem; background: #f0f8f0; border-radius: 4px; text-align: left;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
              <span style="font-weight: 600; color: #2d5016;">Discount Applied:</span>
              <span style="font-weight: 700; color: #2d5016; font-size: 1.2em;" data-discount-amount>${discountFormatted}</span>
            </div>
            <div style="font-size: 0.9em; color: #555; display: flex; align-items: center;">
              <svg aria-hidden="true" focusable="false" class="icon icon-discount" viewBox="0 0 12 12" style="width: 1.2rem; height: 1.2rem; margin-right: 0.7rem; color: rgb(var(--color-foreground));">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
              </svg>
              ${preservedActiveDiscountCode}
            </div>
          </div>
        `;
        this.style.display = 'block';
        this.style.visibility = 'visible';
        return true;
      }
      
      // No active discount and breakdown not showing - hide discount box
      this.innerHTML = '';
      this.style.display = 'none';
      this.style.visibility = 'hidden';
      this.lastValidPrice = null;
      this.stablePriceCount = 0;
      return false;
    }

    parsePrice(priceText) {
      // Remove currency symbols and parse number
      // Handle formats like "$4,549.00 USD" or "$4,549.00"
      const cleaned = priceText.replace(/[^0-9.,]/g, '').replace(/,/g, '');
      return parseFloat(cleaned) || 0;
    }

    formatPrice(amount) {
      // Format as currency (simple version, assumes USD)
      // Amount is already in cents/dollars, so we need to check the format
      // If amount is large (like 235000), it's likely in cents, divide by 100
      // If amount is reasonable (like 2350), it's already in dollars
      const finalAmount = amount > 10000 ? amount / 100 : amount;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(finalAmount);
    }

    calculateDiscountFromBundlePrice(bundleContext, discountCode) {
      if (!bundleContext || !discountCode) {
        return null;
      }

      // Try to get discount from breakdown display first (most accurate)
      const breakdownContainer = bundleContext.querySelector('.bundle-price-breakdown');
      if (breakdownContainer) {
        const storedDiscountAmount = breakdownContainer.dataset.discountAmount;
        if (storedDiscountAmount && parseFloat(storedDiscountAmount) > 0) {
          const discountAmountCents = parseFloat(storedDiscountAmount);
          const discountAmountDollars = discountAmountCents / 100;
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
          }).format(discountAmountDollars);
          
          return {
            discountAmount: discountAmountDollars,
            discountFormatted: `-${formatted} USD`,
            discountType: 'fixed_per_item',
            discountInfo: null
          };
        }
      }
      
      // Fallback: Calculate from discount code mapping (same logic as product-bundle.js)
      const DISCOUNT_MAP = {
        'Subscriber': { type: 'fixed_per_item', amount: 235000 }, // $2,350.00 per item
        'SUBSCRIBER': { type: 'fixed_per_item', amount: 235000 },
        'subscriber': { type: 'fixed_per_item', amount: 235000 },
        'VIP55': { type: 'fixed_per_item', amount: 255400 }, // $2,554.00 per item
        'TELEPORT55': { type: 'percentage', percentage: 0.55 }, // 55% off order
        'TELEPORT50': { type: 'percentage', percentage: 0.50 }, // 50% off order
        'VIP100': { type: 'fixed_order', amount: 10000 }, // $100.00 off order
        'Subscribers': { type: 'fixed_per_item', amount: 190000 }, // $1,900.00 per item
        'VIP': { type: 'fixed_per_item', amount: 210000 }, // $2,100.00 per item
      };
      
      const normalizedCode = discountCode.trim();
      const discountInfo = DISCOUNT_MAP[normalizedCode] || 
                         DISCOUNT_MAP[normalizedCode.toUpperCase()] || 
                         DISCOUNT_MAP[normalizedCode.toLowerCase()];
      
      if (!discountInfo) {
        debugLog('Unknown discount code, cannot calculate discount amount:', discountCode);
        return null;
      }
      
      // Try to get bike price from breakdown display
      const bikePriceElement = bundleContext.querySelector('[data-bundle-bike-price]');
      let bikePriceCents = 0;
      
      if (bikePriceElement) {
        const bikePriceText = bikePriceElement.textContent.trim();
        const cleaned = bikePriceText.replace(/[^0-9.,]/g, '').replace(/,/g, '');
        const dollars = parseFloat(cleaned) || 0;
        bikePriceCents = Math.round(dollars * 100);
      }
      
      let discountAmountCents = 0;
      let discountFormatted = '';
      
      if (discountInfo.type === 'fixed_per_item') {
        discountAmountCents = discountInfo.amount; // Already in cents
        const discountAmountDollars = discountAmountCents / 100;
        discountFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(discountAmountDollars);
      } else if (discountInfo.type === 'fixed_order') {
        discountAmountCents = discountInfo.amount; // Already in cents
        const discountAmountDollars = discountAmountCents / 100;
        discountFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(discountAmountDollars);
      } else if (discountInfo.type === 'percentage' && bikePriceCents > 0) {
        discountAmountCents = Math.round(bikePriceCents * discountInfo.percentage);
        const discountAmountDollars = discountAmountCents / 100;
        discountFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(discountAmountDollars);
      } else {
        // Percentage discount but no bike price yet
        return null;
      }
      
      if (discountAmountCents <= 0) {
        return null;
      }
      
      return {
        discountAmount: discountAmountCents / 100, // Convert to dollars for compatibility
        discountFormatted: `-${discountFormatted} USD`,
        discountType: discountInfo.type,
        discountInfo
      };
    }
  }

  customElements.define('product-discounts', ProductDiscounts);
}
